/**
 * AdminScans (/admin/scans, "Ad-Hoc Scans"): trigger any scan on any active
 * tenant, on mockup carbon/01's brief and carbon/02's table.
 *
 *   1. The brief (BriefHero): the tenant to scan next (never scanned first,
 *      then any not scanned in the last day), its primary button running
 *      that tenant's drift scan.
 *   2. The scan matrix: a DataTable with a row per active tenant and a
 *      32px run button per scan type; a running scan shows a spinner in
 *      its cell. The footer says which scans wait and links the queue and
 *      the errors pages.
 *   3. Scan types: what each one does, and whether it waits for its result.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useTenants } from '@/hooks/useTenants'
import type { TenantDto } from '@/hooks/useTenants'
import type { ActionBrief } from '@/lib/brief-shape'
import { BriefHero } from '@/components/carbon/BriefHero'
import { Button } from '@/components/carbon/Button'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconArrowRight } from '@/components/carbon/icons'
import { IconButton } from '@/components/carbon/IconButton'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Spinner } from '@/components/carbon/Spinner'
import { StructuredList } from '@/components/carbon/StructuredList'
import { Tag } from '@/components/carbon/Tag'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import { composeScanLeadBrief } from '@/components/AdminPortal/page-briefs'

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

type ScanType =
  | 'drift'
  | 'baseline'
  | 'bpa'
  | 'oauth'
  | 'userinsights'
  | 'securescore'

interface ScanTypeConfig {
  key: ScanType
  label: string
  description: string
  // true = fire-and-forget (returns jobId); false = synchronous (await inline)
  async: boolean
}

const SCAN_TYPES: ScanTypeConfig[] = [
  { key: 'drift', label: 'Drift', description: 'Configuration drift scan. SharePoint and OneDrive only on Hybrid tenants.', async: true },
  { key: 'baseline', label: 'Baseline refresh', description: 'Refresh the baseline snapshot.', async: true },
  { key: 'bpa', label: 'Best Practice', description: 'Best Practice evaluation, about 5 minutes.', async: false },
  { key: 'oauth', label: 'OAuth posture', description: 'Queue an OAuth app sync.', async: true },
  { key: 'userinsights', label: 'User analytics', description: 'Refresh user insight data.', async: true },
  { key: 'securescore', label: 'Secure Score', description: 'Refresh Secure Score data.', async: false },
]

// Per-cell state for in-flight scans. The BPA cell uses this to show an inline
// spinner while awaiting the synchronous call; async types rely on a toast.
interface CellState {
  inFlight: boolean
}

type ScanMatrix = Map<string, Map<ScanType, CellState>>

const LOAD_FAILED_BRIEF: ActionBrief = {
  headline: 'Reload the page to see your tenants.',
  lede: '**The tenant list did not load**, so Guardian cannot offer any scan.',
}

// ----------------------------------------------------------------------------
// AdminScans
// ----------------------------------------------------------------------------

export default function AdminScans() {
  const { tenants, isLoading: tenantsLoading, isError: tenantsError } = useTenants()

  // Current user's email — needed as `requestedBy` for baseline refresh.
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: () => api.getCurrentUser(),
    staleTime: 5 * 60 * 1000,
  })

  // Per-cell in-flight state. Keyed by `${tenantId}:${scanType}`.
  const [scanMatrix, setScanMatrix] = useState<ScanMatrix>(new Map())

  // Filter: active tenants only by default. Operators rarely need to scan
  // deactivated tenants; surface them via a toggle if needed in a future pass.
  const activeTenants = tenants.filter(t => t.isActive)

  function setCellState(tenantId: string, scan: ScanType, state: Partial<CellState>) {
    setScanMatrix(prev => {
      const next = new Map(prev)
      const row = new Map(next.get(tenantId) ?? [])
      const current = row.get(scan) ?? { inFlight: false }
      row.set(scan, { ...current, ...state })
      next.set(tenantId, row)
      return next
    })
  }

  function getCellState(tenantId: string, scan: ScanType): CellState {
    return scanMatrix.get(tenantId)?.get(scan) ?? { inFlight: false }
  }

  // ── Scan dispatch ──────────────────────────────────────────────────────────

  async function handleScan(tenant: TenantDto, scan: ScanType) {
    const { tenantId, name } = tenant
    setCellState(tenantId, scan, { inFlight: true })

    try {
      switch (scan) {
        case 'drift': {
          const result = await api.scanTenant(tenantId)
          toast.success(
            <span>
              Drift scan queued for <strong>{name}</strong>.{' '}
              <Link to="/admin/queue" className="underline">Watch in queue</Link>
            </span>
          )
          void result // jobId available if needed
          break
        }

        case 'baseline': {
          const requestedBy = currentUser?.email ?? 'platform-admin'
          const result = await api.refreshBaseline(tenantId, requestedBy)
          toast.success(
            <span>
              Baseline refresh queued for <strong>{name}</strong>.{' '}
              <Link to="/admin/queue" className="underline">Watch in queue</Link>
            </span>
          )
          void result
          break
        }

        case 'bpa': {
          // Synchronous — 5-min timeout baked into the api method. The endpoint
          // returns HTTP 200 even on internal failure (status 'Failed') or when no
          // standards are enabled ('NoStandards'), so branch on the result rather
          // than treating any 200 as success.
          const r = await api.triggerComplianceEvaluation(tenantId)
          if (r.status === 'Failed') {
            toast.error(r.message || `Best Practice evaluation failed for ${name}. Check /admin/errors.`)
          } else if (r.status === 'NoStandards') {
            toast.error(`No compliance standards are enabled for ${name}.`)
          } else if (r.status === 'Partial') {
            toast(`Best Practice evaluation partly finished for ${name}.`)
          } else {
            toast.success(`Best Practice evaluation complete for ${name}.`)
          }
          break
        }

        case 'oauth': {
          // syncOAuthApps only ENQUEUES a background sync job (returns void) and
          // the collection runs server-side afterward — report it as started, not
          // complete, so a queued sync isn't mistaken for a finished one.
          await api.syncOAuthApps(tenantId)
          toast.success(`OAuth posture sync started for ${name}.`)
          break
        }

        case 'userinsights': {
          const result = await api.refreshUserInsights(tenantId)
          toast.success(
            <span>
              User Analytics refresh queued for <strong>{name}</strong>.{' '}
              <Link to="/admin/queue" className="underline">Watch in queue</Link>
            </span>
          )
          void result
          break
        }

        case 'securescore': {
          // refreshSecureScore is synchronous — it returns the summary inline (no
          // jobId, so nothing to watch in the queue) and returns null on 404/500
          // instead of throwing.
          const result = await api.refreshSecureScore(tenantId)
          if (result === null) {
            toast.error(`Secure Score refresh failed for ${name}. Check /admin/errors for details.`)
          } else {
            toast.success(`Secure Score refreshed for ${name}.`)
          }
          break
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error'
      toast.error(`${SCAN_TYPES.find(s => s.key === scan)?.label ?? scan} failed for ${name}: ${message}`)
    } finally {
      setCellState(tenantId, scan, { inFlight: false })
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const { brief, lead } = composeScanLeadBrief(activeTenants, new Date(), tenants.length - activeTenants.length)
  const leadBusy = !!lead && getCellState(lead.tenantId, 'drift').inFlight

  const columns: DataColumn<TenantDto>[] = [
    {
      id: 'tenant',
      header: 'Tenant',
      primary: true,
      cell: tenant => (
        <>
          <TableItem title={tenant.name} sub={tenant.domain} />
          {/* Auth mode hint — helps operator understand which scans are relevant */}
          {tenant.authenticationMode && (
            <Tag kind="outline" size="sm" className="mt-1">
              {tenant.authenticationMode === 'MultiTenant' ? 'Multi-tenant' : 'Per-tenant'}
            </Tag>
          )}
        </>
      ),
    },
    // One cell per scan type
    ...SCAN_TYPES.map<DataColumn<TenantDto>>(s => ({
      id: s.key,
      header: <span title={s.description}>{s.label}</span>,
      nowrap: false,
      cell: tenant => {
        const { inFlight } = getCellState(tenant.tenantId, s.key)
        return (
          <span className="flex items-center gap-2">
            <ScanButton
              label={s.label}
              tenantName={tenant.domain ?? tenant.name}
              inFlight={inFlight}
              async={s.async}
              onClick={() => handleScan(tenant, s.key)}
            />
            {/* Phones stack the cells, so each run button keeps its scan's name beside it there. */}
            <span aria-hidden="true" className="text-helper text-text-secondary c-md:hidden">
              {s.label}
            </span>
          </span>
        )
      },
    })),
  ]

  return (
    <div>
      <section aria-labelledby="scans-verdict" className="mt-8">
        <BriefHero
          brief={tenantsError ? LOAD_FAILED_BRIEF : brief}
          size="pillar"
          headingId="scans-verdict"
          isLoading={tenantsLoading}
          secondaryAction={
            lead && !tenantsError ? (
              <Button
                kind="primary"
                icon={leadBusy ? <Spinner /> : <IconArrowRight />}
                disabled={leadBusy}
                onClick={() => handleScan(lead, 'drift')}
              >
                {`Run the drift scan on ${lead.name}`}
              </Button>
            ) : undefined
          }
        />
      </section>

      {tenantsError ? (
        <InlineNotification kind="error" title="Tenants did not load." className="mt-12">
          Reload to try again.
        </InlineNotification>
      ) : (
        <>
          <DataTable
            className="mt-12"
            title="Scans by tenant"
            description="Run any scan on an active tenant. Queued scans return at once; Best Practice and Secure Score wait for their result."
            notices={
              <InlineNotification kind="info" title="Drift scans on Hybrid tenants.">
                They cover SharePoint and OneDrive only. Guardian watches Entra, Exchange, Intune, Teams, Defender and
                Purview continuously there, so those need no manual trigger.
              </InlineNotification>
            }
            columns={columns}
            rows={activeTenants}
            rowId={tenant => tenant.tenantId}
            rowLabel={tenant => tenant.name}
            isLoading={tenantsLoading}
            loadingRows={3}
            empty="No active tenants found. Onboard a tenant in the admin portal before you run scans."
            footer={
              <p className="m-0">
                Best Practice and Secure Score wait for their result; the other scans queue and return.{' '}
                <Link to="/admin/queue" className="text-link hover:text-link-hover hover:underline">
                  Monitor the job queue
                </Link>{' '}
                for queued results, or{' '}
                <Link to="/admin/errors" className="text-link hover:text-link-hover hover:underline">
                  review errors
                </Link>{' '}
                if a scan fails.
              </p>
            }
          />

          {/* Legend */}
          <Tile as="section" aria-labelledby="scan-types-title" className="mt-8">
            <TileHeader title="Scan types" id="scan-types-title" />
            <StructuredList
              timeColumn={0}
              className="mt-3"
              aria-label="What each scan does"
              rows={SCAN_TYPES.map(s => ({
                id: s.key,
                title: <span className="font-semibold">{s.label}</span>,
                sub: s.description,
                tag: <Tag size="sm">{s.async ? 'Queues' : 'Waits for its result'}</Tag>,
              }))}
            />
          </Tile>
        </>
      )}
    </div>
  )
}

// ----------------------------------------------------------------------------
// ScanButton — individual trigger cell
// ----------------------------------------------------------------------------

interface ScanButtonProps {
  label: string
  tenantName: string
  inFlight: boolean
  async: boolean
  onClick: () => void
}

function ScanButton({ label, tenantName, inFlight, async: isAsync, onClick }: ScanButtonProps) {
  if (inFlight) {
    return (
      <span
        className="inline-grid h-8 w-8 place-items-center text-text-primary"
        aria-label={`${label} running for ${tenantName}`}
        title={isAsync ? 'Queuing…' : 'Running…'}
        role="status"
      >
        <Spinner />
      </span>
    )
  }

  return (
    <IconButton
      size={32}
      onClick={onClick}
      title={`Run ${label} for ${tenantName}`}
      label={`Run ${label} for ${tenantName}`}
      icon={
        /* Play triangle */
        <svg width={16} height={16} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M4 2.5v11L13 8z" />
        </svg>
      }
    />
  )
}
